import { Dispatch, SetStateAction } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { EmploymentType, IncomeVerificationType, incomeVerificationFormSchema } from '@brighte/validation';
import { useGetOccupationDataQuery } from '@graphql/index';
import { ManualOccupationJobTitleMatchingType, getFormFields, isFieldExists } from '../../utils';
import Employed from './Employed';
import SelfEmployed from './SelfEmployed';

type AdditionalEmploymentFieldsProps = {
  financialProductId: string;
  setOccupationIncomeThreshold: Dispatch<SetStateAction<number>>;
  usingOpenBanking?: boolean;
  manualOccupationJobTitle: ManualOccupationJobTitleMatchingType;
  setManualOccupationJobTitle: Dispatch<SetStateAction<ManualOccupationJobTitleMatchingType>>;
};

function AdditionalEmploymentFields({
  financialProductId,
  setOccupationIncomeThreshold,
  usingOpenBanking,
  manualOccupationJobTitle,
  setManualOccupationJobTitle,
}: AdditionalEmploymentFieldsProps) {
  const { control, setValue, register, clearErrors } = useFormContext<IncomeVerificationType>();
  const incomeType = useWatch({ control, name: 'applicant.employment.type' });

  const incomeFields = getFormFields(incomeVerificationFormSchema, financialProductId);

  const isOccupationFieldDisplayed =
    isFieldExists(incomeFields, 'applicant.employment.employed.code') &&
    isFieldExists(incomeFields, 'applicant.employment.selfEmployed.code');

  const { data: occupations, isLoading: occupationLoading } = useGetOccupationDataQuery(
    {},
    {
      enabled: isOccupationFieldDisplayed,
      select: data => data.getOccupationData,
    },
  );
  if (!incomeType) return null;

  const employmentFields = {
    [EmploymentType.EMPLOYED]: (
      <Employed
        fields={incomeFields}
        control={control}
        setValue={setValue}
        register={register}
        clearErrors={clearErrors}
        setOccupationIncomeThreshold={setOccupationIncomeThreshold}
        occupations={occupations ?? []}
        isOccupationLoading={occupationLoading}
        isOccupationFieldDisplayed={isOccupationFieldDisplayed}
        usingOpenBanking={usingOpenBanking}
        manualOccupationJobTitle={manualOccupationJobTitle}
        setManualOccupationJobTitle={setManualOccupationJobTitle}
      />
    ),
    [EmploymentType.SELF_EMPLOYED]: (
      <SelfEmployed
        control={control}
        setValue={setValue}
        fields={incomeFields}
        register={register}
        clearErrors={clearErrors}
        setOccupationIncomeThreshold={setOccupationIncomeThreshold}
        occupations={occupations ?? []}
        isOccupationLoading={occupationLoading}
        isOccupationFieldDisplayed={isOccupationFieldDisplayed}
        usingOpenBanking={usingOpenBanking}
        manualOccupationJobTitle={manualOccupationJobTitle}
        setManualOccupationJobTitle={setManualOccupationJobTitle}
      />
    ),
    [EmploymentType.PENSIONER]: null,
    [EmploymentType.OTHER]: null,
  };

  return employmentFields[incomeType] ?? null;
}

export default AdditionalEmploymentFields;
